import { compareBib } from './age-groups';
import {
  RIFLE_TOURNAMENT_ID,
  RIFLE_TOURNAMENT_SLUG,
  TEN_TENTHS,
  formatDecimalTenths,
  formatMaxDisplay,
  formatPointsTenths,
  isParseError,
  summarizeShooter,
  tournamentFormat,
  wholeNumberScoring,
  type ShooterGender,
  type ShootingFormat,
  type StoredScore,
  type StoredShooter,
  type StoredTournament,
} from './shooting';

export { WHOLE_SHOT_MESSAGE, parseWholeShotPoints, wholeNumberScoring } from './shooting';

/** Round 1 teams exist for the seeded rifle tournament. Other tournaments stay individual. */
export function round1TeamsEnabled(tournament: { id?: string | null; slug?: string | null } | null | undefined): boolean {
  if (!tournament) return false;
  return tournament.id === RIFLE_TOURNAMENT_ID || tournament.slug === RIFLE_TOURNAMENT_SLUG;
}

export const ROUND1_DEFAULT_TEAM_SIZE = 6;
export const ROUND1_DEFAULT_SCORED_SHOTS = 10;
export const ROUND1_DEFAULT_TRIAL_SHOTS = 3;
export const ROUND2_DEFAULT_QUALIFYING_TEAMS = 6;
export const ROUND1_SHOT_MAX = 10;

export function round1TeamSize(tournament: { round1TeamSize?: number | null }): number {
  return positiveInt(tournament.round1TeamSize, 1, 30) ?? ROUND1_DEFAULT_TEAM_SIZE;
}

export function round1TrialShots(tournament: { round1TrialShots?: number | null }): number {
  return positiveInt(tournament.round1TrialShots, 0, 20) ?? ROUND1_DEFAULT_TRIAL_SHOTS;
}

export function qualifyingTeamCount(tournament: { round2QualifyingTeams?: number | null }): number {
  return positiveInt(tournament.round2QualifyingTeams, 0, 40) ?? ROUND2_DEFAULT_QUALIFYING_TEAMS;
}

function positiveInt(value: unknown, min: number, max: number): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max ? value : null;
}

/** How many empty teams to create: one team per `teamSize` shooters, at least one when anyone is on the roster. */
export function defaultTeamCount(shooterCount: number, teamSize: number): number {
  if (shooterCount <= 0) return 0;
  const size = Math.max(1, teamSize);
  return Math.ceil(shooterCount / size);
}

export function defaultTeamName(index: number): string {
  return `Team ${index + 1}`;
}

export interface WholeShotEntry {
  draft: string;
  /** Move to the next box immediately (0, 2–9, or 10). */
  advance: boolean;
  /** Reserved for callers that support a delayed advance. */
  armWait: boolean;
}

/** Preserve the input so invalid pasted decimals are rejected instead of truncated. */
export function normalizeWholeShotDraft(value: string): string {
  return value.trim();
}

/**
 * What one keystroke (or a pasted value) does in a whole-number shot box.
 * "1" waits so "10" can be typed. "10", "0", and "2"–"9" advance at once.
 */
export function nextWholeShotEntry(value: string): WholeShotEntry {
  const draft = normalizeWholeShotDraft(value);
  if (draft === '10' || /^[02-9]$/.test(draft)) return { draft, advance: true, armWait: false };
  if (draft === '1') return { draft, advance: false, armWait: false };
  return { draft, advance: false, armWait: false };
}

/** Reject an invalid keystroke/paste without turning it into a different score. */
export function strictWholeShotEntry(raw: string, previousDraft: string): { draft: string; advance: boolean; rejected: boolean } {
  const entry = nextWholeShotEntry(raw);
  if (entry.draft && !/^(?:[0-9]|10)$/.test(entry.draft)) return { draft: previousDraft, advance: false, rejected: true };
  return { draft: entry.draft, advance: entry.advance, rejected: false };
}

export function showShotPoints(tenths: number, whole: boolean): string {
  if (!whole) return formatDecimalTenths(tenths);
  if (tenths % 10 === 0) return formatPointsTenths(tenths);
  return formatDecimalTenths(tenths);
}

export function showMax(format: ShootingFormat, whole: boolean): string {
  if (!whole) return formatMaxDisplay(format);
  return String(format.countedShots * ROUND1_SHOT_MAX);
}

function parseCount(value: unknown, label: string, min: number, max: number): { ok: true; n: number } | { ok: false; error: string } {
  const raw =
    typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : typeof value === 'string'
        ? value.trim()
        : '';
  if (!raw) return { ok: false, error: `Enter ${label}.` };
  if (!/^\d+$/.test(raw)) return { ok: false, error: `${label} must be a whole number.` };
  const n = Number(raw);
  if (n < min || n > max) return { ok: false, error: `${label} must be from ${min} to ${max}.` };
  return { ok: true, n };
}

export interface Round1SettingsInput {
  teamSize: number;
  scoredShots: number;
  trialShots: number;
  qualifyingTeams: number;
}

export function parseRound1Settings(input: {
  teamSize: unknown;
  scoredShots: unknown;
  trialShots: unknown;
  qualifyingTeams: unknown;
}): { ok: true; settings: Round1SettingsInput } | { ok: false; error: string } {
  const teamSize = parseCount(input.teamSize, 'Team size', 1, 30);
  if (isParseError(teamSize)) return teamSize;
  const scoredShots = parseCount(input.scoredShots, 'Scored shots', 1, 60);
  if (isParseError(scoredShots)) return scoredShots;
  const trialShots = parseCount(input.trialShots, 'Trial shots', 0, 20);
  if (isParseError(trialShots)) return trialShots;
  const qualifyingTeams = parseCount(input.qualifyingTeams, 'Teams in Round 2', 0, 40);
  if (isParseError(qualifyingTeams)) return qualifyingTeams;
  return {
    ok: true,
    settings: {
      teamSize: teamSize.n,
      scoredShots: scoredShots.n,
      trialShots: trialShots.n,
      qualifyingTeams: qualifyingTeams.n,
    },
  };
}

/** Null in the database means the default, so saving the default does not write a number. */
export function storedRound1Option(value: number, fallback: number): number | null {
  return value === fallback ? null : value;
}

export interface Round1TeamInput {
  teamId: string;
  name: string;
  sortOrder: number;
  totalTenths: number;
  /** Shots of 10 across the team. Breaks a tie on the total. */
  tens: number;
}

export interface Round1TeamStanding {
  rank: number;
  teamId: string;
  name: string;
  totalTenths: number;
  totalDisplay: string;
  tens: number;
  memberCount: number;
  members: Round1TeamMember[];
}

export interface Round1TeamMember {
  shooterId: string;
  name: string;
  bib: string | null;
  gender: ShooterGender | null;
  totalDisplay: string;
  totalTenths: number;
  tens: number;
  shotsFired: number;
}

export interface RankedTeam {
  teamId: string;
  name: string;
  totalTenths: number;
  tens: number;
  sortOrder: number;
}

export interface TeamCut {
  count: number;
  /** In Round 2 with no one tied on the last place. */
  qualified: RankedTeam[];
  /** Tied for the last place, and not all of them fit. */
  tied: RankedTeam[];
  /** How many of `tied` still need an admin to choose them. */
  slots: number;
}

function sameScore(a: RankedTeam, b: RankedTeam): boolean {
  return a.totalTenths === b.totalTenths && a.tens === b.tens;
}

/**
 * Top `count` teams. Higher total first, then more shots of 10.
 * A tie that still straddles the last place is not broken: those teams are returned for an admin to choose.
 * Sort order only affects display inside a tie. It never decides who qualifies.
 */
export function selectTopTeams(teams: readonly RankedTeam[], count: number): TeamCut {
  const ranked = [...teams].sort(
    (a, b) => b.totalTenths - a.totalTenths || b.tens - a.tens || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
  if (count <= 0) return { count: 0, qualified: [], tied: [], slots: 0 };
  const qualified: RankedTeam[] = [];
  let index = 0;
  while (index < ranked.length && qualified.length < count) {
    const group = [ranked[index]!];
    let next = index + 1;
    while (next < ranked.length && sameScore(ranked[next]!, group[0]!)) {
      group.push(ranked[next]!);
      next += 1;
    }
    const room = count - qualified.length;
    if (group.length <= room) {
      qualified.push(...group);
      index = next;
      continue;
    }
    return { count, qualified, tied: group, slots: room };
  }
  return { count, qualified, tied: [], slots: 0 };
}

export function qualifyingTeamIds(cut: TeamCut, picks: readonly string[]): string[] {
  const tiedIds = new Set(cut.tied.map((team) => team.teamId));
  const picksOk = cut.slots > 0 && picks.length === cut.slots && picks.every((id) => tiedIds.has(id));
  const chosen = picksOk ? picks : [];
  return [...cut.qualified.map((team) => team.teamId), ...chosen];
}

export interface Round1MemberScore {
  shooterId: string;
  teamId: string | null;
  name: string;
  bib: string | null;
  gender: ShooterGender | null;
  totalTenths: number;
  tens: number;
  shotsFired: number;
}

export function memberScores(
  shooters: readonly StoredShooter[],
  scores: readonly StoredScore[],
  members: readonly { shooterId: string; teamId: string }[],
  format: ShootingFormat,
): Round1MemberScore[] {
  const teamOf = new Map(members.map((member) => [member.shooterId, member.teamId]));
  return shooters.map((shooter) => {
    const summary = summarizeShooter(
      scores.filter((score) => score.shooterId === shooter.id),
      format,
    );
    return {
      shooterId: shooter.id,
      teamId: teamOf.get(shooter.id) ?? null,
      name: shooter.name,
      bib: shooter.bib,
      gender: shooter.gender ?? null,
      totalTenths: summary.totalTenths,
      tens: summary.tens,
      shotsFired: summary.shotsFired,
    };
  });
}

export function rankRound1Teams(
  teams: readonly { id: string; name: string; sortOrder: number }[],
  people: readonly Round1MemberScore[],
  whole: boolean,
): Round1TeamStanding[] {
  const built = teams
    .map((team) => {
      const members = people
        .filter((person) => person.teamId === team.id)
        .sort((a, b) => b.totalTenths - a.totalTenths || compareBib(a.bib, b.bib) || a.name.localeCompare(b.name));
      const totalTenths = members.reduce((sum, person) => sum + person.totalTenths, 0);
      const tens = members.reduce((sum, person) => sum + person.tens, 0);
      return { team, members, totalTenths, tens };
    })
    .sort(
      (a, b) =>
        b.totalTenths - a.totalTenths ||
        b.tens - a.tens ||
        a.team.sortOrder - b.team.sortOrder ||
        a.team.name.localeCompare(b.team.name),
    );

  let lastRank = 0;
  let previous: { totalTenths: number; tens: number } | null = null;
  const hasShots = people.some((person) => person.teamId && person.shotsFired > 0);
  return built.map((row, index) => {
    const tied = previous != null && previous.totalTenths === row.totalTenths && previous.tens === row.tens;
    const rank = tied ? lastRank : index + 1;
    lastRank = rank;
    previous = { totalTenths: row.totalTenths, tens: row.tens };
    return {
      rank: hasShots ? rank : 0,
      teamId: row.team.id,
      name: row.team.name,
      totalTenths: row.totalTenths,
      totalDisplay: hasShots ? showShotPoints(row.totalTenths, whole) : '—',
      tens: row.tens,
      memberCount: row.members.length,
      members: row.members.map((person) => ({
        shooterId: person.shooterId,
        name: person.name,
        bib: person.bib,
        gender: person.gender,
        totalDisplay: showShotPoints(person.totalTenths, whole),
        totalTenths: person.totalTenths,
        tens: person.tens,
        shotsFired: person.shotsFired,
      })),
    };
  });
}

export function round1RankedForCut(standings: readonly Round1TeamStanding[], sortOrderOf: (teamId: string) => number): RankedTeam[] {
  return standings.map((team) => ({
    teamId: team.teamId,
    name: team.name,
    totalTenths: team.totalTenths,
    tens: team.tens,
    sortOrder: sortOrderOf(team.teamId),
  }));
}

/** Read-time qualification: retained Round 2 rows need not be deleted when the cut changes. */
export function currentQualifyingTeamIds(
  teams: readonly { id: string; name: string; sortOrder: number }[],
  people: readonly Round1MemberScore[],
  tournament: StoredTournament,
  picks: readonly string[],
): Set<string> {
  const order = new Map(teams.map((team) => [team.id, team.sortOrder]));
  const standings = rankRound1Teams(teams, people, wholeNumberScoring(tournament));
  const cut = selectTopTeams(round1RankedForCut(standings, (id) => order.get(id) ?? 0), qualifyingTeamCount(tournament));
  return new Set(qualifyingTeamIds(cut, picks));
}

/** A shot of 10 counts toward the tie-break. Legacy 10.0 (100 tenths) counts too. */
export function isTen(tenths: number): boolean {
  return tenths >= TEN_TENTHS;
}

export function formatForTournament(tournament: StoredTournament): ShootingFormat {
  return tournamentFormat(tournament);
}

export interface Round1AdminView {
  teamSize: number;
  scoredShots: number;
  countedShots: number;
  trialShots: number;
  qualifyingTeams: number;
  wholeNumber: boolean;
  /** True when the saved format counts every shot. */
  allShotsCount: boolean;
  teams: Round1TeamStanding[];
  unassigned: {
    shooterId: string;
    name: string;
    bib: string | null;
    gender: ShooterGender | null;
    totalDisplay: string;
  }[];
  tie: { slots: number; selectedTeamIds: string[]; teams: { teamId: string; name: string; totalDisplay: string; tens: number }[] } | null;
}
